import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, Input, NgZone, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { firstValueFrom, Subscription } from 'rxjs';
import { User } from '../../services/auth.service';
import { AssistantService } from '../../services/assistant.service';
import { WebsiteManagementService } from '../../services/website-management.service';
import { WebsiteBuilderApiService } from '../../services/website-builder-api.service';
import { ComponentStudioApiService } from '../../services/component-studio-api.service';
import { ComponentCanvasThemeApiService } from '../../services/component-canvas-theme-api.service';
import { AccountService, CreateAddressRequest, UpdateAddressRequest, UpdateProfileRequest } from '../../services/account.service';
import { PaddleCheckoutEvent, PaddleJsService } from '../../services/paddle-js.service';
import { AssistantDisplayMode, AssistantUiStateService } from '../../services/assistant-ui-state.service';
import {
  AssistantAction,
  AssistantConversationSummary,
  AssistantCreditCheckout,
  AssistantCreditPack,
  AssistantMessage,
  AssistantMeteringState,
  AssistantPendingImage,
  AssistantToolActivity,
  AssistantUsageRecord
} from '../../models/assistant.models';
import { StudioComponentDefinition } from '../../models/component-studio.models';
import { CanvasThemePackage } from '../../models/component-canvas-theme.models';
import { WebsitePageDocument } from '../../models/website-builder.models';
import { StoreEmailTemplateKey } from '../../models/website-management.models';

type AssistantConfirmation =
  | { kind: 'delete-conversation'; conversation: AssistantConversationSummary }
  | { kind: 'apply-action'; action: AssistantAction }
  | { kind: 'apply-actions'; actions: AssistantAction[] };

type AssistantPresentation = 'floating' | 'page';
type AssistantApplyMode = 'review' | 'auto';
type AssistantTextReplacement = {
  find: string;
  replace: string;
  caseSensitive: boolean;
};
type PendingAssistantImageAttachment = {
  id: string;
  fileName: string;
  state: 'optimizing' | 'ready' | 'failed';
  previewLoaded: boolean;
  image?: AssistantPendingImage;
  error?: string;
};

const PAGE_DOCUMENT_TEXT_FIELDS = [
  'eyebrow', 'title', 'text', 'secondaryText', 'buttonText', 'badgeText',
  'mediaLabel', 'caption', 'mediaAlt'
] as const;

@Component({
  selector: 'app-ai-assistant',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './ai-assistant.component.html',
  styleUrls: ['./ai-assistant.component.scss']
})
export class AiAssistantComponent implements OnChanges, OnDestroy {
  @Input() user: User | null = null;
  @Input() presentation: AssistantPresentation = 'floating';
  @ViewChild('messageList') private messageList?: ElementRef<HTMLElement>;
  @ViewChild('fileInput') private fileInput?: ElementRef<HTMLInputElement>;

  isOpen = false;
  isStreaming = false;
  isConversationLoading = false;
  showHistory = false;
  showPreferences = false;
  showCredits = false;
  isCreditCheckoutBusy = false;
  creditPacks: AssistantCreditPack[] = [];
  conversations: AssistantConversationSummary[] = [];
  activeConversationId: string | null = null;
  messages: AssistantMessage[] = [];
  draft = '';
  pendingImages: PendingAssistantImageAttachment[] = [];
  metering: AssistantMeteringState | null = null;
  statusMessage = '';
  errorMessage = '';
  renameConversationId: string | null = null;
  renameDraft = '';
  appliedActions = new Set<string>();
  applyingActions = new Set<string>();
  confirmation: AssistantConfirmation | null = null;
  displayMode: AssistantDisplayMode = 'floating';
  applyMode: AssistantApplyMode = 'review';
  private abortController: AbortController | null = null;
  private sentHistory: string[] = [];
  private historyIndex = -1;
  private objectUrls = new Set<string>();
  private readonly localActionRetentionMs = 30 * 24 * 60 * 60 * 1000;
  private activeCreditCheckout: AssistantCreditCheckout | null = null;
  private isCreditCheckoutCompleting = false;
  private streamingDeltaMessage: AssistantMessage | null = null;
  private streamingDeltaBuffer = '';
  private streamingDeltaTimer: number | null = null;
  private autoApplyQueue: Promise<void> = Promise.resolve();
  private initializedUserPublicId: string | null = null;
  private conversationSelectionVersion = 0;
  private pendingImageSequence = 0;
  private readonly pendingImageSourceUrls = new Map<string, string>();
  private readonly layoutSubscription: Subscription;

  constructor(
    private readonly assistantService: AssistantService,
    private readonly websiteManagementService: WebsiteManagementService,
    private readonly websiteBuilderApiService: WebsiteBuilderApiService,
    private readonly componentStudioApiService: ComponentStudioApiService,
    private readonly componentCanvasThemeApiService: ComponentCanvasThemeApiService,
    private readonly accountService: AccountService,
    private readonly paddleJsService: PaddleJsService,
    private readonly assistantUiStateService: AssistantUiStateService,
    private readonly router: Router,
    private readonly ngZone: NgZone,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {
    this.layoutSubscription = this.assistantUiStateService.layoutState$.subscribe((layout) => {
      this.displayMode = layout.displayMode;
      if (this.presentation !== 'page') {
        this.isOpen = layout.isOpen;
      }
      this.changeDetectorRef.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['user']) {
      if (this.user) {
        if (this.initializedUserPublicId === this.user.publicId) {
          return;
        }
        this.initializedUserPublicId = this.user.publicId;
        this.appliedActions = this.readLocallyAppliedActions();
        this.restorePreferences();
        this.activeConversationId = this.assistantUiStateService.getActiveConversation(this.user.publicId);
        this.isOpen = this.presentation === 'page' || this.assistantUiStateService.layoutState.isOpen;
        void this.loadWorkspaceState();
      } else {
        this.resetWorkspaceState();
      }
    }
  }

  ngOnDestroy(): void {
    this.layoutSubscription.unsubscribe();
    this.abortController?.abort();
    this.flushStreamingDeltas();
    this.paddleJsService.closeCheckout();
    this.clearPendingImages();
    this.revokeObjectUrls();
  }

  toggle(): void {
    if (this.presentation === 'page') return;
    this.isOpen = !this.isOpen;
    this.assistantUiStateService.setOpen(this.isOpen);
    this.errorMessage = '';
    if (this.isOpen && !this.metering) {
      void this.loadWorkspaceState();
    }
    if (this.isOpen) {
      this.scrollMessagesToBottom();
    }
  }

  newConversation(): void {
    if (this.isStreaming || this.isConversationLoading) return;
    this.conversationSelectionVersion += 1;
    this.isConversationLoading = false;
    this.setActiveConversation(null);
    this.messages = [];
    this.draft = '';
    this.errorMessage = '';
    this.statusMessage = '';
    this.clearPendingImages();
    this.revokeObjectUrls();
    this.showHistory = false;
  }

  async selectConversation(conversationId: string, forceLoad = false): Promise<void> {
    if (this.isStreaming || this.isConversationLoading) return;
    if (!forceLoad && conversationId === this.activeConversationId && this.messages.length > 0) {
      this.runInAngular(() => {
        this.showHistory = false;
      });
      return;
    }
    const selectionVersion = ++this.conversationSelectionVersion;
    // Switch immediately so request and image latency never leave the history view stuck.
    this.runInAngular(() => {
      this.revokeObjectUrls();
      this.setActiveConversation(conversationId);
      this.messages = [];
      this.isConversationLoading = true;
      this.showHistory = false;
      this.errorMessage = '';
    });
    try {
      const state = await firstValueFrom(this.assistantService.getConversation(conversationId));
      if (selectionVersion !== this.conversationSelectionVersion) return;
      const messages = state.messages;
      this.runInAngular(() => {
        this.setActiveConversation(state.conversation.publicId);
        this.messages = messages;
        this.appliedActions = new Set([
          ...this.readLocallyAppliedActions(),
          ...this.messages.flatMap((message) => message.actions.filter((action) => action.status === 'applied').map((action) => action.id))
        ]);
        this.isConversationLoading = false;
        requestAnimationFrame(() => this.scrollMessagesToBottom());
      });
      void this.hydrateMessageImages(messages, selectionVersion).then(() => {
        if (selectionVersion !== this.conversationSelectionVersion) return;
        this.runInAngular(() => {
          this.messages = [...messages];
        });
      });
    } catch (error) {
      if (selectionVersion !== this.conversationSelectionVersion) return;
      this.runInAngular(() => {
        this.isConversationLoading = false;
        this.errorMessage = this.errorText(error);
      });
    }
  }

  beginRename(conversation: AssistantConversationSummary): void {
    this.renameConversationId = conversation.publicId;
    this.renameDraft = conversation.title;
  }

  cancelRename(): void {
    this.renameConversationId = null;
    this.renameDraft = '';
  }

  async saveRename(): Promise<void> {
    if (!this.renameConversationId || !this.renameDraft.trim()) return;
    try {
      const updated = await firstValueFrom(this.assistantService.renameConversation(this.renameConversationId, this.renameDraft));
      this.conversations = this.conversations.map((conversation) => conversation.publicId === updated.publicId ? updated : conversation);
      this.cancelRename();
    } catch (error) {
      this.errorMessage = this.errorText(error);
    }
  }

  requestDeleteConversation(conversation: AssistantConversationSummary): void {
    if (this.isStreaming) return;
    this.confirmation = { kind: 'delete-conversation', conversation };
  }

  async confirmDeleteConversation(conversation: AssistantConversationSummary): Promise<void> {
    try {
      await firstValueFrom(this.assistantService.deleteConversation(conversation.publicId));
      this.conversations = this.conversations.filter((entry) => entry.publicId !== conversation.publicId);
      if (this.activeConversationId === conversation.publicId) this.newConversation();
    } catch (error) {
      this.errorMessage = this.errorText(error);
    }
  }

  dismissConfirmation(): void {
    this.confirmation = null;
  }

  async confirmPendingAction(): Promise<void> {
    const confirmation = this.confirmation;
    this.confirmation = null;
    if (!confirmation) return;
    if (confirmation.kind === 'delete-conversation') {
      await this.confirmDeleteConversation(confirmation.conversation);
      return;
    }
    if (confirmation.kind === 'apply-action') {
      await this.applyConfirmedAction(confirmation.action);
      return;
    }
    await this.applyConfirmedActions(confirmation.actions);
  }

  triggerImagePicker(): void {
    this.fileInput?.nativeElement.click();
  }

  async chooseImages(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    await this.addFiles(Array.from(input.files ?? []));
    input.value = '';
  }

  async handlePaste(event: ClipboardEvent): Promise<void> {
    const files = Array.from(event.clipboardData?.files ?? []).filter((file) => file.type.startsWith('image/'));
    if (files.length === 0) return;
    event.preventDefault();
    await this.addFiles(files);
  }

  get hasPendingImageProcessing(): boolean {
    return this.pendingImages.some((image) => image.state === 'optimizing');
  }

  get hasPendingImageFailures(): boolean {
    return this.pendingImages.some((image) => image.state === 'failed');
  }

  removePendingImage(imageId: string): void {
    this.revokePendingImageSourceUrl(imageId);
    this.pendingImages = this.pendingImages.filter((image) => image.id !== imageId);
  }

  markPendingImagePreviewLoaded(imageId: string): void {
    this.updatePendingImage(imageId, (image) => ({ ...image, previewLoaded: true }));
  }

  markMessageImagePreviewLoaded(image: AssistantMessage['images'][number]): void {
    if (image.previewState === 'ready') return;
    image.previewState = 'ready';
    this.messages = [...this.messages];
  }

  markMessageImagePreviewFailed(image: AssistantMessage['images'][number]): void {
    if (image.previewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(image.previewUrl);
      this.objectUrls.delete(image.previewUrl);
    }
    image.previewUrl = undefined;
    image.previewState = 'failed';
    this.messages = [...this.messages];
  }

  async send(): Promise<void> {
    const message = this.draft.trim();
    if (this.isStreaming || this.hasPendingImageProcessing) {
      if (this.hasPendingImageProcessing) this.errorMessage = 'Wait for the attached image to finish optimizing.';
      return;
    }
    if (this.hasPendingImageFailures) {
      this.errorMessage = 'Remove the image that could not be optimized before sending.';
      return;
    }
    const pendingImages = this.pendingImages.flatMap((attachment) => attachment.image ? [attachment.image] : []);
    if (!message && pendingImages.length === 0) return;
    if (!this.metering?.canContinue) {
      this.errorMessage = this.metering?.blockingReason ?? 'BizzWeaver AI usage is currently unavailable.';
      this.showCredits = true;
      return;
    }

    const localMessage: AssistantMessage = {
      publicId: `local-user-${Date.now()}`,
      role: 'user',
      content: message,
      createdAt: new Date().toISOString(),
      actions: [],
      images: this.pendingImages.map((attachment) => ({
        publicId: `local-image-${attachment.id}`,
        fileName: attachment.image!.fileName,
        contentType: 'image/webp',
        byteSize: attachment.image!.byteSize,
        previewUrl: attachment.image!.dataUrl,
        previewState: 'loading'
      })),
      usage: null
    };
    const assistantMessage: AssistantMessage = {
      publicId: `local-assistant-${Date.now()}`,
      role: 'assistant',
      content: '',
      createdAt: new Date().toISOString(),
      actions: [],
      images: [],
      toolActivities: [],
      usage: null
    };

    this.messages = [...this.messages, localMessage, assistantMessage];
    if (message) this.sentHistory = [...this.sentHistory.filter((entry) => entry !== message), message].slice(-40);
    this.historyIndex = -1;
    this.draft = '';
    this.clearPendingImages();
    this.errorMessage = '';
    this.isStreaming = true;
    this.statusMessage = 'Preparing your workspace request...';
    const abortController = new AbortController();
    this.abortController = abortController;
    this.scrollMessagesToBottom();

    try {
      await this.assistantService.streamChat({
        conversationId: this.activeConversationId ?? undefined,
        contextWebsiteId: this.currentWebsiteId() ?? undefined,
        message,
        images: pendingImages
      }, {
        onConversation: (conversationId) => {
          this.runInAngular(() => this.setActiveConversation(conversationId));
        },
        onDelta: (delta) => this.queueStreamingDelta(assistantMessage, delta),
        onAction: (action) => {
          this.runInAngular(() => {
            this.flushStreamingDeltas();
            assistantMessage.actions = [...assistantMessage.actions, action];
            this.messages = [...this.messages];
            this.scrollMessagesToBottom();
            this.queueAutomaticApply(action);
          });
        },
        onTool: (activity) => {
          this.runInAngular(() => {
            this.flushStreamingDeltas();
            this.recordToolActivity(assistantMessage, activity);
            this.messages = [...this.messages];
            this.scrollMessagesToBottom();
          });
        },
        onUsage: (usage) => {
          this.runInAngular(() => {
            this.flushStreamingDeltas();
            assistantMessage.usage = usage;
            this.messages = [...this.messages];
          });
        },
        onStatus: (status) => {
          this.runInAngular(() => this.statusMessage = status);
        },
        onDone: (messageId) => {
          this.runInAngular(() => {
            this.flushStreamingDeltas();
            if (messageId) assistantMessage.publicId = messageId;
            this.statusMessage = '';
          });
        },
        onError: (messageText) => {
          this.runInAngular(() => {
            this.flushStreamingDeltas();
            this.errorMessage = messageText;
            this.statusMessage = '';
            if (!assistantMessage.content) assistantMessage.content = 'I could not complete that request.';
            this.messages = [...this.messages];
          });
        }
      }, abortController.signal);
    } catch (error) {
      this.flushStreamingDeltas();
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        this.errorMessage = this.errorText(error);
      }
      if (!assistantMessage.content) assistantMessage.content = abortController.signal.aborted ? 'Response stopped.' : 'I could not complete that request.';
      this.messages = [...this.messages];
    } finally {
      this.runInAngular(() => {
        this.flushStreamingDeltas();
        this.isStreaming = false;
        this.statusMessage = '';
        this.abortController = null;
      });
      await this.refreshConversations();
      await this.refreshMetering();
      this.runInAngular(() => this.scrollMessagesToBottom());
    }
  }

  stop(): void {
    this.abortController?.abort();
    this.statusMessage = 'Stopping response...';
  }

  handleInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void this.send();
      return;
    }
    if ((event.key === 'ArrowUp' || event.key === 'ArrowDown') && !this.draft.includes('\n')) {
      if (event.key === 'ArrowUp') {
        if (this.historyIndex < this.sentHistory.length - 1) this.historyIndex += 1;
      } else if (this.historyIndex >= 0) {
        this.historyIndex -= 1;
      }
      if (this.historyIndex >= 0) {
        this.draft = this.sentHistory[this.sentHistory.length - 1 - this.historyIndex] ?? this.draft;
      } else if (event.key === 'ArrowDown') {
        this.draft = '';
      }
    }
  }

  async buyCreditPack(pack: AssistantCreditPack): Promise<void> {
    if (this.isCreditCheckoutBusy) return;
    this.errorMessage = '';
    this.isCreditCheckoutBusy = true;
    try {
      const checkout = await firstValueFrom(this.assistantService.prepareCreditCheckout(pack.publicId));
      this.activeCreditCheckout = checkout;
      await this.paddleJsService.openOverlayCheckout({
        clientToken: checkout.clientToken,
        environment: checkout.environment,
        customerId: checkout.customerId,
        customerAuthToken: checkout.customerAuthToken,
        priceId: checkout.priceId,
        customData: checkout.customData,
        onEvent: (event) => this.runInAngular(() => this.handleCreditCheckoutEvent(event))
      });
      this.statusMessage = 'Secure Paddle checkout is open. AI tokens are added only after payment completes.';
    } catch (error) {
      this.errorMessage = this.errorText(error);
      this.activeCreditCheckout = null;
    } finally {
      this.isCreditCheckoutBusy = false;
    }
  }

  requestApplyAction(action: AssistantAction): void {
    if (this.applyingActions.has(action.id) || this.appliedActions.has(action.id)) return;
    this.confirmation = { kind: 'apply-action', action };
  }

  requestApplyAll(message: AssistantMessage): void {
    const actions = this.pendingActions(message);
    if (actions.length < 2) return;
    this.confirmation = { kind: 'apply-actions', actions };
  }

  pendingActions(message: AssistantMessage): AssistantAction[] {
    return message.actions.filter((action) => !this.appliedActions.has(action.id) && !this.applyingActions.has(action.id));
  }

  async applyConfirmedActions(actions: AssistantAction[]): Promise<void> {
    for (const action of actions) {
      await this.applyConfirmedAction(action);
      if (this.errorMessage) return;
    }
  }

  private async applyConfirmedAction(action: AssistantAction): Promise<void> {
    if (this.applyingActions.has(action.id) || this.appliedActions.has(action.id)) return;
    this.runInAngular(() => {
      this.applyingActions.add(action.id);
      this.errorMessage = '';
      this.messages = [...this.messages];
    });
    try {
      await this.applyWorkspaceAction(action);
      this.runInAngular(() => {
        action.status = 'applied';
        this.appliedActions.add(action.id);
        this.messages = [...this.messages];
      });
      this.rememberLocallyAppliedAction(action.id);
      this.runInAngular(() => {
        this.statusMessage = action.type === 'update_page_document'
          || action.type === 'update_website_page_documents'
          || action.type === 'replace_website_page_text'
          ? 'Draft change applied. Review it in the builder, then publish the website when you want it live.'
          : 'Change applied. The affected BizzWeaver workspace is ready to review.';
      });
      try {
        await firstValueFrom(this.assistantService.markActionApplied(action.id));
      } catch {
        // The workspace change already succeeded. Keep it disabled locally so a
        // temporary history failure never encourages the user to apply it twice.
        this.statusMessage = 'Change applied. Its assistant history could not be updated yet, so do not apply this proposal again.';
      }
      await this.refreshConversations();
    } catch (error) {
      this.runInAngular(() => this.errorMessage = this.errorText(error));
    } finally {
      this.runInAngular(() => {
        this.applyingActions.delete(action.id);
        this.messages = [...this.messages];
      });
    }
  }

  isAutomaticAction(action: AssistantAction): boolean {
    return this.applyMode === 'auto' && this.isAutoApplicable(action);
  }

  setApplyMode(mode: AssistantApplyMode): void {
    this.applyMode = mode;
    this.persistPreferences();
  }

  setDisplayMode(mode: AssistantDisplayMode): void {
    this.displayMode = mode;
    this.assistantUiStateService.setDisplayMode(mode);
    this.persistPreferences();
  }

  openUsagePage(): void {
    const url = this.router.serializeUrl(this.router.createUrlTree(['/ai/usage']));
    window.open(url, '_blank', 'noopener');
  }

  openFullChat(): void {
    void this.router.navigate(['/ai/chat']);
    this.showPreferences = false;
  }

  actionButtonLabel(action: AssistantAction): string {
    if (this.appliedActions.has(action.id)) return 'Applied';
    if (this.applyingActions.has(action.id)) return 'Applying...';
    return action.type === 'publish_website' ? 'Review and publish' : 'Review and apply';
  }

  private queueAutomaticApply(action: AssistantAction): void {
    if (!this.isAutomaticAction(action)) return;
    this.autoApplyQueue = this.autoApplyQueue.then(async () => {
      await this.applyConfirmedAction(action);
    });
  }

  private isAutoApplicable(action: AssistantAction): boolean {
    return [
      'create_website', 'update_website', 'create_page', 'update_page', 'update_page_document',
      'update_website_page_documents', 'replace_website_page_text', 'update_data_collections', 'update_translations',
      'update_email_settings', 'update_email_template', 'create_component', 'update_component',
      'update_canvas_theme', 'save_website_version', 'save_custom_template', 'create_from_custom_template'
    ].includes(action.type);
  }

  renderMarkdown(content: string): string {
    const lines = content.replace(/\r\n?/g, '\n').split('\n');
    const rendered: string[] = [];
    let listItems: string[] = [];
    let orderedList = false;
    let inCodeBlock = false;
    const flushList = () => {
      if (listItems.length === 0) return;
      rendered.push(`<${orderedList ? 'ol' : 'ul'}>${listItems.map((item) => `<li>${this.renderInlineMarkdown(item)}</li>`).join('')}</${orderedList ? 'ol' : 'ul'}>`);
      listItems = [];
    };

    for (const line of lines) {
      if (line.trim().startsWith('```')) {
        flushList();
        inCodeBlock = !inCodeBlock;
        rendered.push(inCodeBlock ? '<pre><code>' : '</code></pre>');
        continue;
      }
      if (inCodeBlock) {
        rendered.push(`${this.escapeHtml(line)}\n`);
        continue;
      }
      const unordered = /^\s*[-*+]\s+(.+)$/.exec(line);
      const ordered = /^\s*\d+\.\s+(.+)$/.exec(line);
      if (unordered || ordered) {
        const nextOrdered = Boolean(ordered);
        if (listItems.length > 0 && orderedList !== nextOrdered) flushList();
        orderedList = nextOrdered;
        listItems.push((unordered ?? ordered)![1]);
        continue;
      }
      flushList();
      const heading = /^(#{1,3})\s+(.+)$/.exec(line);
      if (heading) {
        const level = heading[1].length;
        rendered.push(`<h${level}>${this.renderInlineMarkdown(heading[2])}</h${level}>`);
      } else if (/^>\s?/.test(line)) {
        rendered.push(`<blockquote>${this.renderInlineMarkdown(line.replace(/^>\s?/, ''))}</blockquote>`);
      } else if (line.trim()) {
        rendered.push(`<p>${this.renderInlineMarkdown(line)}</p>`);
      }
    }
    flushList();
    return rendered.join('') + (inCodeBlock ? '</code></pre>' : '');
  }

  formatTokens(tokens: number): string {
    return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(tokens);
  }

  formatUsdFromNano(nanoUsd: number): string {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 4 }).format(nanoUsd / 1_000_000_000);
  }

  formatCreditPackPrice(pack: AssistantCreditPack): string {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: pack.currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(pack.priceCents / 100);
  }

  formatTimestamp(timestamp: string): string {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(date);
  }

  usageSummary(usage: AssistantUsageRecord): string {
    const parts = [
      `${this.formatTokens(usage.inputTokens)} in (${this.formatTokens(usage.uncachedInputTokens)} uncached, ${this.formatTokens(usage.cacheWriteInputTokens)} cache write, ${this.formatTokens(usage.cachedInputTokens)} cached)`,
      `${this.formatTokens(usage.outputTokens)} out (${this.formatTokens(usage.textOutputTokens)} text, ${this.formatTokens(usage.reasoningTokens)} reasoning)`,
      `${this.formatUsdFromNano(usage.estimatedCostNanoUsd)} provider estimate`,
      `${usage.requestCount} ${usage.requestCount === 1 ? 'request' : 'requests'}`
    ];
    return parts.join(' · ');
  }

  private queueStreamingDelta(message: AssistantMessage, delta: string): void {
    this.streamingDeltaMessage = message;
    this.streamingDeltaBuffer += delta;
    if (this.streamingDeltaTimer) return;
    this.streamingDeltaTimer = window.setTimeout(() => this.flushStreamingDeltas(), 32);
  }

  private flushStreamingDeltas(): void {
    if (this.streamingDeltaTimer) {
      clearTimeout(this.streamingDeltaTimer);
      this.streamingDeltaTimer = null;
    }
    const message = this.streamingDeltaMessage;
    const delta = this.streamingDeltaBuffer;
    this.streamingDeltaMessage = null;
    this.streamingDeltaBuffer = '';
    if (!message || !delta) return;
    this.ngZone.run(() => {
      message.content += delta;
      this.messages = [...this.messages];
      this.scrollMessagesToBottom();
      this.changeDetectorRef.detectChanges();
    });
  }

  trackConversation(_index: number, conversation: AssistantConversationSummary): string {
    return conversation.publicId;
  }

  trackMessage(_index: number, message: AssistantMessage): string {
    return message.publicId;
  }

  private async loadWorkspaceState(): Promise<void> {
    await Promise.all([this.refreshConversations(), this.refreshMetering(), this.refreshCreditPacks()]);
    const activeConversationId = this.activeConversationId;
    if (activeConversationId && this.messages.length === 0 && this.conversations.some((entry) => entry.publicId === activeConversationId)) {
      await this.selectConversation(activeConversationId, true);
    }
  }

  private async refreshConversations(): Promise<void> {
    try {
      const conversations = await firstValueFrom(this.assistantService.listConversations());
      this.runInAngular(() => {
        this.conversations = conversations;
      });
    } catch (error) {
      this.runInAngular(() => {
        this.errorMessage = this.errorText(error);
      });
    }
  }

  private async refreshMetering(): Promise<void> {
    try {
      const metering = await firstValueFrom(this.assistantService.getMetering());
      this.runInAngular(() => {
        this.metering = metering;
      });
    } catch (error) {
      this.runInAngular(() => {
        this.errorMessage = this.errorText(error);
      });
    }
  }

  private async refreshCreditPacks(): Promise<void> {
    try {
      const creditPacks = await firstValueFrom(this.assistantService.listCreditPacks());
      this.runInAngular(() => {
        this.creditPacks = creditPacks;
      });
    } catch (error) {
      this.runInAngular(() => {
        this.errorMessage = this.errorText(error);
      });
    }
  }

  private handleCreditCheckoutEvent(event: PaddleCheckoutEvent): void {
    if (event.name === 'checkout.completed') {
      void this.completeCreditCheckout(event);
      return;
    }
    if (event.name === 'checkout.error' || event.name === 'checkout.payment.error') {
      this.errorMessage = event.detail || 'Paddle could not complete the AI credit purchase. No tokens were added.';
      this.activeCreditCheckout = null;
      return;
    }
    if (event.name === 'checkout.closed' && !this.isCreditCheckoutCompleting) {
      this.activeCreditCheckout = null;
      this.statusMessage = '';
    }
  }

  private async completeCreditCheckout(event: PaddleCheckoutEvent): Promise<void> {
    const checkout = this.activeCreditCheckout;
    const customerId = event.data?.customer?.id;
    const transactionId = event.data?.transaction_id;
    if (!checkout || !customerId || !transactionId || this.isCreditCheckoutCompleting) {
      this.errorMessage = 'Paddle completed checkout without the details needed to add AI tokens. Refresh this panel in a moment.';
      return;
    }
    this.isCreditCheckoutCompleting = true;
    this.statusMessage = 'Confirming your AI token purchase...';
    try {
      for (let attempt = 0; attempt < 6; attempt += 1) {
        try {
          this.metering = await firstValueFrom(this.assistantService.completeCreditCheckout({
            checkoutSessionId: checkout.checkoutSessionId,
            customerId,
            transactionId
          }));
          this.errorMessage = '';
          this.statusMessage = 'AI tokens added to your prepaid balance.';
          this.showCredits = false;
          await this.refreshCreditPacks();
          return;
        } catch (error) {
          if (attempt === 5 || !this.errorText(error).includes('still being finalized')) {
            throw error;
          }
          await new Promise<void>((resolve) => window.setTimeout(resolve, 1500));
        }
      }
    } catch (error) {
      this.errorMessage = this.errorText(error);
      this.statusMessage = '';
    } finally {
      this.isCreditCheckoutCompleting = false;
      this.activeCreditCheckout = null;
      this.changeDetectorRef.detectChanges();
    }
  }

  private runInAngular(callback: () => void): void {
    this.ngZone.run(() => {
      callback();
      this.changeDetectorRef.detectChanges();
    });
  }

  private async hydrateMessageImages(messages: AssistantMessage[], selectionVersion: number): Promise<void> {
    const images = messages.flatMap((message) => message.images);
    const workerCount = Math.min(4, images.length);
    let nextIndex = 0;
    const hydrateNext = async (): Promise<void> => {
      while (nextIndex < images.length) {
        const image = images[nextIndex++];
        this.runInAngular(() => {
          image.previewState = 'loading';
          this.messages = [...messages];
        });
        try {
          const url = await this.assistantService.getImageUrl(image.publicId);
          if (selectionVersion !== this.conversationSelectionVersion) {
            URL.revokeObjectURL(url);
            return;
          }
          this.runInAngular(() => {
            image.previewUrl = url;
            image.previewState = 'loading';
            this.objectUrls.add(url);
            this.messages = [...messages];
          });
        } catch {
          if (selectionVersion !== this.conversationSelectionVersion) return;
          this.runInAngular(() => {
            image.previewUrl = undefined;
            image.previewState = 'failed';
            this.messages = [...messages];
          });
        }
      }
    };
    await Promise.all(Array.from({ length: workerCount }, () => hydrateNext()));
  }

  private async addFiles(files: File[]): Promise<void> {
    if (files.length === 0) return;
    this.errorMessage = '';
    for (const file of files) {
      if (this.pendingImages.length >= 4) {
        this.errorMessage = 'A message can include up to four images.';
        break;
      }
      if (!file.type.startsWith('image/')) {
        this.errorMessage = 'Only image files can be sent to BizzWeaver AI.';
        continue;
      }
      const id = this.createPendingImageId();
      const sourceUrl = URL.createObjectURL(file);
      this.pendingImageSourceUrls.set(id, sourceUrl);
      this.pendingImages = [...this.pendingImages, { id, fileName: file.name, state: 'optimizing', previewLoaded: false }];
      void this.optimizePendingImage(id, file, sourceUrl);
    }
  }

  private async optimizePendingImage(imageId: string, file: File, sourceUrl: string): Promise<void> {
    try {
      const image = await this.convertImageToWebp(file, sourceUrl);
      this.revokePendingImageSourceUrl(imageId);
      this.runInAngular(() => this.updatePendingImage(imageId, (attachment) => ({
        ...attachment,
        image,
        state: 'ready',
        previewLoaded: false,
        error: undefined
      })));
    } catch (error) {
      this.revokePendingImageSourceUrl(imageId);
      this.runInAngular(() => this.updatePendingImage(imageId, (attachment) => ({
        ...attachment,
        state: 'failed',
        error: this.errorText(error)
      })));
    }
  }

  private async convertImageToWebp(file: File, sourceUrl: string): Promise<AssistantPendingImage> {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('The image could not be read.'));
      element.src = sourceUrl;
    });
    const longestEdge = Math.max(image.naturalWidth, image.naturalHeight);
    const initialEdge = Math.min(longestEdge, 1600);
    let blob = await this.encodeImageAsWebp(image, initialEdge, 0.82);
    if (blob.size > 1_250_000) {
      const lowerQualityBlob = await this.encodeImageAsWebp(image, initialEdge, 0.76);
      if (lowerQualityBlob.size < blob.size) blob = lowerQualityBlob;
    }
    if (blob.size > 1_250_000 && initialEdge > 960) {
      const reducedEdge = Math.max(960, Math.round(initialEdge * Math.max(0.72, Math.sqrt(1_250_000 / blob.size))));
      const reducedBlob = await this.encodeImageAsWebp(image, reducedEdge, 0.78);
      if (reducedBlob.size < blob.size) blob = reducedBlob;
    }
    if (blob.size > 2 * 1024 * 1024) throw new Error('Use a smaller image. Assistant images must be at most 2 MB after optimization.');
    return {
      fileName: `${file.name.replace(/\.[^/.]+$/, '') || 'assistant-image'}.webp`,
      dataUrl: await this.readAsDataUrl(blob),
      byteSize: blob.size
    };
  }

  private async encodeImageAsWebp(image: HTMLImageElement, longestEdge: number, quality: number): Promise<Blob> {
    const originalLongestEdge = Math.max(image.naturalWidth, image.naturalHeight);
    const scale = originalLongestEdge > longestEdge ? longestEdge / originalLongestEdge : 1;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const context = canvas.getContext('2d');
    if (!context) throw new Error('The browser could not prepare this image.');
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/webp', quality));
    if (!blob) throw new Error('The browser could not optimize this image.');
    return blob;
  }

  private createPendingImageId(): string {
    return globalThis.crypto?.randomUUID?.() ?? `pending-image-${Date.now()}-${++this.pendingImageSequence}`;
  }

  private updatePendingImage(imageId: string, update: (image: PendingAssistantImageAttachment) => PendingAssistantImageAttachment): void {
    const index = this.pendingImages.findIndex((image) => image.id === imageId);
    if (index === -1) return;
    this.pendingImages = this.pendingImages.map((image, candidateIndex) => candidateIndex === index ? update(image) : image);
  }

  private revokePendingImageSourceUrl(imageId: string): void {
    const sourceUrl = this.pendingImageSourceUrls.get(imageId);
    if (sourceUrl) URL.revokeObjectURL(sourceUrl);
    this.pendingImageSourceUrls.delete(imageId);
  }

  private clearPendingImages(): void {
    this.pendingImageSourceUrls.forEach((sourceUrl) => URL.revokeObjectURL(sourceUrl));
    this.pendingImageSourceUrls.clear();
    this.pendingImages = [];
  }

  private readAsDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => typeof reader.result === 'string' ? resolve(reader.result) : reject(new Error('The image could not be prepared.'));
      reader.onerror = () => reject(new Error('The image could not be prepared.'));
      reader.readAsDataURL(blob);
    });
  }

  private currentWebsiteId(): string | null {
    const match = /^\/websites\/([0-9a-f-]{20,})/i.exec(this.router.url.split('?')[0]);
    return match?.[1] ?? null;
  }

  private async applyWorkspaceAction(action: AssistantAction): Promise<void> {
    const payload = action.payload as Record<string, unknown>;
    switch (action.type) {
      case 'create_website':
        await firstValueFrom(this.websiteManagementService.createWebsite(payload as unknown as Parameters<WebsiteManagementService['createWebsite']>[0]));
        return;
      case 'update_website':
        if (!action.websiteId) throw new Error('This proposal does not identify a website.');
        await firstValueFrom(this.websiteManagementService.updateWebsite(action.websiteId, payload));
        return;
      case 'delete_website':
        if (!action.websiteId) throw new Error('This proposal does not identify a website.');
        await firstValueFrom(this.websiteManagementService.deleteWebsite(action.websiteId));
        if (this.currentWebsiteId() === action.websiteId) await this.router.navigate(['/websites']);
        return;
      case 'create_page':
        if (!action.websiteId) throw new Error('This proposal does not identify a website.');
        await firstValueFrom(this.websiteManagementService.createPage(action.websiteId, payload as unknown as Parameters<WebsiteManagementService['createPage']>[1]));
        return;
      case 'update_page':
        if (!action.websiteId || !action.pageId) throw new Error('This proposal does not identify a page.');
        await firstValueFrom(this.websiteManagementService.updatePage(
          action.websiteId,
          action.pageId,
          payload as unknown as Parameters<WebsiteManagementService['updatePage']>[2]
        ));
        return;
      case 'delete_page':
        if (!action.websiteId || !action.pageId) throw new Error('This proposal does not identify a page.');
        await firstValueFrom(this.websiteManagementService.deletePage(action.websiteId, action.pageId));
        return;
      case 'update_page_document':
        if (!action.websiteId || !action.pageId || !payload['document']) throw new Error('This proposal is missing its page document.');
        await firstValueFrom(this.websiteBuilderApiService.updateDraft(
          action.websiteId,
          action.pageId,
          await this.applyAssistantImagePlacements(action.websiteId, payload['document'] as WebsitePageDocument, payload['imagePlacements'])
        ));
        return;
      case 'update_website_page_documents': {
        if (!action.websiteId || !Array.isArray(payload['updates'])) throw new Error('This proposal is missing its page documents.');
        const updates = payload['updates'];
        for (let index = 0; index < updates.length; index += 1) {
          const update = updates[index];
          if (!update || typeof update !== 'object' || typeof (update as Record<string, unknown>)['pageId'] !== 'string' || !(update as Record<string, unknown>)['document']) {
            throw new Error(`Page document ${index + 1} is incomplete.`);
          }
          this.runInAngular(() => this.statusMessage = `Applying draft ${index + 1} of ${updates.length}...`);
          await firstValueFrom(this.websiteBuilderApiService.updateDraft(
            action.websiteId,
            (update as Record<string, unknown>)['pageId'] as string,
            await this.applyAssistantImagePlacements(
              action.websiteId,
              (update as Record<string, unknown>)['document'] as WebsitePageDocument,
              (update as Record<string, unknown>)['imagePlacements']
            )
          ));
        }
        return;
      }
      case 'replace_website_page_text': {
        if (!action.websiteId) throw new Error('This proposal does not identify a website.');
        const { pageIds, replacements } = this.parseWebsitePageTextReplacement(payload);
        let changedPageCount = 0;
        for (let index = 0; index < pageIds.length; index += 1) {
          this.runInAngular(() => this.statusMessage = `Updating page ${index + 1} of ${pageIds.length}...`);
          const draft = await firstValueFrom(this.websiteBuilderApiService.getDraft(action.websiteId, pageIds[index]));
          const result = this.replaceWebsitePageDocumentText(draft.document, replacements);
          if (!result.changed) continue;
          await firstValueFrom(this.websiteBuilderApiService.updateDraft(action.websiteId, pageIds[index], result.document));
          changedPageCount += 1;
        }
        if (changedPageCount === 0) {
          throw new Error('The current page drafts no longer contain the text in this proposal. No draft was changed.');
        }
        return;
      }
      case 'restore_page_revision': {
        const revisionId = typeof payload['revisionId'] === 'string' ? payload['revisionId'] : null;
        if (!action.websiteId || !action.pageId || !revisionId) throw new Error('This proposal is missing its page revision.');
        await firstValueFrom(this.websiteManagementService.restorePageRevision(action.websiteId, action.pageId, revisionId));
        return;
      }
      case 'update_data_collections':
        if (!action.websiteId) throw new Error('This proposal does not identify a website.');
        await firstValueFrom(this.websiteManagementService.updateDataCollections(action.websiteId, payload as unknown as Parameters<WebsiteManagementService['updateDataCollections']>[1]));
        return;
      case 'update_translations':
        if (!action.websiteId) throw new Error('This proposal does not identify a website.');
        await firstValueFrom(this.websiteManagementService.updateTranslations(
          action.websiteId,
          payload as unknown as Parameters<WebsiteManagementService['updateTranslations']>[1]
        ));
        return;
      case 'update_email_settings':
        if (!action.websiteId) throw new Error('This proposal does not identify a website.');
        await firstValueFrom(this.websiteManagementService.updateEmailSettings(
          action.websiteId,
          payload as unknown as Parameters<WebsiteManagementService['updateEmailSettings']>[1]
        ));
        return;
      case 'update_email_template': {
        const templateKey = typeof payload['templateKey'] === 'string' ? payload['templateKey'] as StoreEmailTemplateKey : null;
        const template = payload['template'];
        if (!action.websiteId || !templateKey || !template || typeof template !== 'object') {
          throw new Error('This proposal is missing its email-template details.');
        }
        await firstValueFrom(this.websiteManagementService.updateEmailTemplate(
          action.websiteId,
          templateKey,
          template as Parameters<WebsiteManagementService['updateEmailTemplate']>[2]
        ));
        return;
      }
      case 'create_component': {
        const definition = payload['definition'] as StudioComponentDefinition | undefined;
        if (!definition) throw new Error('This proposal is missing its component definition.');
        const definitions = await firstValueFrom(this.componentStudioApiService.getState());
        await firstValueFrom(this.componentStudioApiService.saveState([...definitions, { ...definition, source: 'custom' }]));
        return;
      }
      case 'update_component': {
        const definition = payload['definition'] as StudioComponentDefinition | undefined;
        if (!definition) throw new Error('This proposal is missing its component definition.');
        const definitions = await firstValueFrom(this.componentStudioApiService.getState());
        if (!definitions.some((entry) => entry.id === definition.id)) throw new Error('The component is no longer available.');
        await firstValueFrom(this.componentStudioApiService.saveState(definitions.map((entry) => entry.id === definition.id ? { ...definition, source: 'custom' } : entry)));
        return;
      }
      case 'delete_component': {
        const componentId = typeof payload['componentId'] === 'string' ? payload['componentId'] : null;
        if (!componentId) throw new Error('This proposal does not identify a component.');
        const definitions = await firstValueFrom(this.componentStudioApiService.getState());
        if (!definitions.some((entry) => entry.id === componentId)) throw new Error('The component is no longer available.');
        await firstValueFrom(this.componentStudioApiService.saveState(definitions.filter((entry) => entry.id !== componentId)));
        return;
      }
      case 'update_canvas_theme': {
        const themePackage = payload['themePackage'] as CanvasThemePackage | undefined;
        if (!themePackage?.id) throw new Error('This proposal is missing its canvas theme package.');
        const themePackages = await firstValueFrom(this.componentCanvasThemeApiService.getPackages());
        const updated = themePackages.some((entry) => entry.id === themePackage.id)
          ? themePackages.map((entry) => entry.id === themePackage.id ? themePackage : entry)
          : [...themePackages, themePackage];
        await firstValueFrom(this.componentCanvasThemeApiService.savePackages(updated));
        return;
      }
      case 'save_website_version':
        if (!action.websiteId) throw new Error('This proposal does not identify a website.');
        await firstValueFrom(this.websiteManagementService.createWebsiteVersion(action.websiteId, typeof payload['note'] === 'string' ? payload['note'] : action.summary));
        return;
      case 'restore_website_version': {
        const versionId = typeof payload['versionId'] === 'string' ? payload['versionId'] : null;
        if (!action.websiteId || !versionId) throw new Error('This proposal is missing its website version.');
        await firstValueFrom(this.websiteManagementService.restoreWebsiteVersion(action.websiteId, versionId));
        return;
      }
      case 'save_custom_template':
        if (!action.websiteId) throw new Error('This proposal does not identify a website.');
        await firstValueFrom(this.assistantService.saveCustomTemplate(
          action.websiteId,
          typeof payload['name'] === 'string' ? payload['name'] : action.title,
          typeof payload['description'] === 'string' ? payload['description'] : null
        ));
        return;
      case 'update_custom_template': {
        const templateId = typeof payload['templateId'] === 'string' ? payload['templateId'] : null;
        if (!templateId) throw new Error('This proposal does not identify a reusable template.');
        await firstValueFrom(this.assistantService.updateCustomTemplate(templateId, payload));
        return;
      }
      case 'delete_custom_template': {
        const templateId = typeof payload['templateId'] === 'string' ? payload['templateId'] : null;
        if (!templateId) throw new Error('This proposal does not identify a reusable template.');
        await firstValueFrom(this.assistantService.deleteCustomTemplate(templateId));
        return;
      }
      case 'update_account_profile':
        await firstValueFrom(this.accountService.updateProfile((payload['profile'] ?? payload) as UpdateProfileRequest));
        return;
      case 'create_account_address':
        await firstValueFrom(this.accountService.createAddress((payload['address'] ?? payload) as CreateAddressRequest));
        return;
      case 'update_account_address': {
        const addressId = typeof payload['addressId'] === 'string' ? payload['addressId'] : null;
        if (!addressId) throw new Error('This proposal does not identify an address.');
        await firstValueFrom(this.accountService.updateAddress(addressId, (payload['address'] ?? payload) as UpdateAddressRequest));
        return;
      }
      case 'delete_account_address': {
        const addressId = typeof payload['addressId'] === 'string' ? payload['addressId'] : null;
        if (!addressId) throw new Error('This proposal does not identify an address.');
        await firstValueFrom(this.accountService.deleteAddress(addressId));
        return;
      }
      case 'publish_website':
        if (!action.websiteId) throw new Error('This proposal does not identify a website.');
        await firstValueFrom(this.websiteManagementService.publishWebsite(action.websiteId));
        return;
      case 'create_from_custom_template': {
        const templateId = typeof payload['templateId'] === 'string' ? payload['templateId'] : null;
        const name = typeof payload['name'] === 'string' ? payload['name'] : null;
        if (!templateId || !name) throw new Error('This proposal needs a reusable template and a website name.');
        const website = await firstValueFrom(this.assistantService.createWebsiteFromCustomTemplate(templateId, {
          name,
          slug: typeof payload['slug'] === 'string' ? payload['slug'] : undefined,
          description: typeof payload['description'] === 'string' ? payload['description'] : null
        }));
        await this.router.navigate(['/websites', website.publicId]);
        return;
      }
      default:
        throw new Error('This assistant proposal is not supported by this BizzWeaver workspace.');
    }
  }

  private async applyAssistantImagePlacements(
    websiteId: string,
    document: WebsitePageDocument,
    rawPlacements: unknown
  ): Promise<WebsitePageDocument> {
    if (!Array.isArray(rawPlacements) || rawPlacements.length === 0) return document;

    const nextDocument = structuredClone(document);
    for (const placement of rawPlacements) {
      if (!placement || typeof placement !== 'object') throw new Error('An image placement is invalid.');
      const values = placement as Record<string, unknown>;
      const imageId = typeof values['imageId'] === 'string' ? values['imageId'] : null;
      const nodeId = typeof values['nodeId'] === 'string' ? values['nodeId'] : null;
      const altText = typeof values['altText'] === 'string' ? values['altText'] : undefined;
      if (!imageId || !nodeId) throw new Error('An image placement is missing its image or media node.');
      const asset = await firstValueFrom(this.assistantService.promoteImageToWebsiteAsset(imageId, websiteId));
      const node = this.findPageDocumentNode(nextDocument.root, nodeId);
      if (!node || node.type !== 'media') throw new Error('An image placement must target a media node in the proposed page document.');
      node.content = { ...node.content, mediaUrl: asset.url, ...(altText ? { mediaAlt: altText } : {}) };
    }
    return nextDocument;
  }

  private parseWebsitePageTextReplacement(payload: Record<string, unknown>): {
    pageIds: string[];
    replacements: AssistantTextReplacement[];
  } {
    if (!Array.isArray(payload['pageIds']) || !Array.isArray(payload['replacements'])) {
      throw new Error('This proposal is missing its target pages or text replacements.');
    }
    const pageIds = payload['pageIds'].filter((pageId): pageId is string => typeof pageId === 'string' && pageId.length > 0);
    if (pageIds.length === 0 || pageIds.length !== payload['pageIds'].length || new Set(pageIds).size !== pageIds.length) {
      throw new Error('This proposal has invalid target pages.');
    }
    const replacements = payload['replacements'].map((replacement): AssistantTextReplacement => {
      if (!replacement || typeof replacement !== 'object') throw new Error('This proposal has an invalid text replacement.');
      const values = replacement as Record<string, unknown>;
      if (typeof values['find'] !== 'string' || typeof values['replace'] !== 'string'
        || values['find'].trim().length === 0 || values['replace'].trim().length === 0) {
        throw new Error('This proposal has an invalid text replacement.');
      }
      return {
        find: values['find'],
        replace: values['replace'],
        caseSensitive: values['caseSensitive'] === true
      };
    });
    if (replacements.length === 0) throw new Error('This proposal has no text replacements.');
    return { pageIds, replacements };
  }

  private replaceWebsitePageDocumentText(
    document: WebsitePageDocument,
    replacements: AssistantTextReplacement[]
  ): { document: WebsitePageDocument; changed: boolean } {
    const nextDocument = structuredClone(document);
    let changed = false;
    const replace = (value: string): string => {
      let nextValue = value;
      for (const replacement of replacements) {
        if (replacement.caseSensitive) {
          nextValue = nextValue.split(replacement.find).join(replacement.replace);
        } else {
          const escapedFind = replacement.find.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          nextValue = nextValue.replace(new RegExp(escapedFind, 'gi'), () => replacement.replace);
        }
      }
      return nextValue;
    };
    const replaceValue = (value: string): string => {
      const nextValue = replace(value);
      changed ||= nextValue !== value;
      return nextValue;
    };

    nextDocument.title = replaceValue(nextDocument.title);
    nextDocument.description = replaceValue(nextDocument.description);
    const visit = (node: WebsitePageDocument['root']): void => {
      node.label = replaceValue(node.label);
      const content = { ...node.content };
      for (const field of PAGE_DOCUMENT_TEXT_FIELDS) {
        if (typeof content[field] === 'string') content[field] = replaceValue(content[field]);
      }
      node.content = content;
      node.children.forEach(visit);
    };
    visit(nextDocument.root);
    return { document: nextDocument, changed };
  }

  private findPageDocumentNode(node: WebsitePageDocument['root'], nodeId: string): WebsitePageDocument['root'] | null {
    if (node.id === nodeId) return node;
    for (const child of node.children) {
      const match = this.findPageDocumentNode(child, nodeId);
      if (match) return match;
    }
    return null;
  }

  private scrollMessagesToBottom(): void {
    queueMicrotask(() => {
      const element = this.messageList?.nativeElement;
      if (element) element.scrollTop = element.scrollHeight;
    });
  }

  private recordToolActivity(message: AssistantMessage, activity: AssistantToolActivity): void {
    const current = message.toolActivities ?? [];
    const existingIndex = current.findIndex((entry) => entry.id === activity.id);
    message.toolActivities = existingIndex === -1
      ? [...current, activity]
      : current.map((entry, index) => index === existingIndex ? activity : entry);
  }

  private renderInlineMarkdown(value: string): string {
    const escaped = this.escapeHtml(value);
    const withCode = escaped.replace(/`([^`\n]+)`/g, '<code>$1</code>');
    const withStrong = withCode.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
    const withEmphasis = withStrong.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>');
    return withEmphasis.replace(/(https:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>');
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private resetWorkspaceState(): void {
    this.abortController?.abort();
    this.clearPendingImages();
    this.revokeObjectUrls();
    this.isOpen = false;
    this.assistantUiStateService.setOpen(false);
    this.isStreaming = false;
    this.conversations = [];
    this.messages = [];
    if (this.user) this.assistantUiStateService.clearUser(this.user.publicId);
    this.activeConversationId = null;
    this.metering = null;
    this.appliedActions = new Set();
    this.initializedUserPublicId = null;
    this.conversationSelectionVersion += 1;
  }

  private readLocallyAppliedActions(): Set<string> {
    if (!this.user || typeof localStorage === 'undefined') return new Set();
    try {
      const raw = localStorage.getItem(this.appliedActionStorageKey());
      const saved = raw ? JSON.parse(raw) as Record<string, unknown> : {};
      const cutoff = Date.now() - this.localActionRetentionMs;
      const active = Object.entries(saved)
        .filter(([actionId, timestamp]) => /^[0-9a-f-]{20,}$/i.test(actionId) && typeof timestamp === 'number' && timestamp >= cutoff)
        .sort(([, left], [, right]) => Number(right) - Number(left))
        .slice(0, 250);
      const normalized = Object.fromEntries(active);
      localStorage.setItem(this.appliedActionStorageKey(), JSON.stringify(normalized));
      return new Set(active.map(([actionId]) => actionId));
    } catch {
      return new Set();
    }
  }

  private rememberLocallyAppliedAction(actionId: string): void {
    if (!this.user || typeof localStorage === 'undefined') return;
    try {
      const active = this.readLocallyAppliedActions();
      active.add(actionId);
      const entries = [
        [actionId, Date.now()],
        ...[...active].filter((id) => id !== actionId).map((id) => [id, Date.now()])
      ].slice(0, 250);
      localStorage.setItem(this.appliedActionStorageKey(), JSON.stringify(Object.fromEntries(entries)));
    } catch {
      // Local persistence is a duplicate-apply guard, never a requirement for a completed change.
    }
  }

  private appliedActionStorageKey(): string {
    return `bizzweaver.assistant.applied-actions.${this.user?.publicId ?? 'anonymous'}`;
  }

  private setActiveConversation(conversationId: string | null): void {
    this.activeConversationId = conversationId;
    if (this.user) this.assistantUiStateService.setActiveConversation(this.user.publicId, conversationId);
  }

  private restorePreferences(): void {
    if (!this.user || typeof localStorage === 'undefined') return;
    const layout = this.assistantUiStateService.layoutState;
    try {
      const raw = localStorage.getItem(this.preferenceStorageKey());
      const preferences = raw ? JSON.parse(raw) as Record<string, unknown> : {};
      this.displayMode = layout.displayMode;
      this.applyMode = preferences['applyMode'] === 'auto' ? 'auto' : 'review';
    } catch {
      this.displayMode = layout.displayMode;
      this.applyMode = 'review';
    }
  }

  private persistPreferences(): void {
    if (!this.user || typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(this.preferenceStorageKey(), JSON.stringify({
        applyMode: this.applyMode
      }));
    } catch {
      // Presentation preferences are optional and must never block assistant use.
    }
  }

  private preferenceStorageKey(): string {
    return `bizzweaver.assistant.preferences.${this.user?.publicId ?? 'anonymous'}`;
  }

  private revokeObjectUrls(): void {
    this.objectUrls.forEach((url) => URL.revokeObjectURL(url));
    this.objectUrls.clear();
  }

  private errorText(error: unknown): string {
    if (typeof error === 'object' && error && 'message' in error && typeof error.message === 'string') return error.message;
    return 'BizzWeaver AI could not complete that request.';
  }
}
